import React, { useEffect, useRef, useState } from 'react';
import type Editor from '@ckeditor/ckeditor5-core/src/editor/editor';

type Props = {
  value: string;
  onChange: (html: string) => void;
  height?: number;
  disabled?: boolean;
};

const ICON_ENTER =
  'M3 8V3h5v2H5v3H3zm9-5h5v5h-2V5h-3V3zM5 13h3v2H3v-5h2v3zm10-3h2v5h-5v-2h3v-3z';
const ICON_EXIT =
  'M8 8H3V6h3V3h2v5zm4-5h2v3h3v2h-5V3zM6 14v3H4v-5h5v2H6zm10-2v5h-2v-3h-3v-2h5z';

export default function Wysiwyg({
  value,
  onChange,
  height = 360,
  disabled = false,
}: Props) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const lastValueRef = useRef<string>(value);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // sempre l'ultima versione di onChange
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // INIT / DESTROY con import dinamico
  useEffect(() => {
    if (!hostRef.current) return;

    let destroyed = false;

    (async () => {
      try {
        const mod = await import('@ckeditor/ckeditor5-build-classic');

        // trova il build reale
        const Build: any =
          (mod as any).ClassicEditor ??
          (mod as any).default ??
          mod;

        if (destroyed || !hostRef.current) return;

        const editor: any = await Build.create(hostRef.current, {
          toolbar: [
            'undo', 'redo', '|',
            'heading', '|',
            'bold', 'italic', 'link', '|',  // <--- tolto 'underline'
            'bulletedList', 'numberedList', '|',
            'blockQuote', 'insertTable', /* 'imageUpload', */ 'mediaEmbed',
          ],
        });

        if (destroyed) {
          editor.destroy();
          return;
        }


        editorRef.current = editor;

        editor.setData(value || '');
        lastValueRef.current = value || '';

        // read-only iniziale
        if (disabled) {
          editor.enableReadOnlyMode?.('prop-disabled');
        }

        // ascolta i cambi
        editor.model.document.on('change:data', () => {
          const html = editor.getData() ?? '';

          lastValueRef.current = html;
          if (typeof onChangeRef.current === 'function') {
            onChangeRef.current(html);
          }
        });
      } catch (err) {
        console.error('CKEditor INIT ERROR', err);
      }
    })();

    return () => {
      destroyed = true;
      setIsFullscreen(false);
      document.body.style.overflow = '';
      if (editorRef.current) {
        (editorRef.current as any).destroy().catch(() => {});
        editorRef.current = null;
      }
    };
  }, []); // una volta per mount

  // readonly quando cambia `disabled`
  useEffect(() => {
    const ed = editorRef.current as any;
    if (!ed) return;

    if (disabled) {
      ed.enableReadOnlyMode?.('prop-disabled');
    } else {
      ed.disableReadOnlyMode?.('prop-disabled');
    }
  }, [disabled]);


  useEffect(() => {
    const ed = editorRef.current as any;
    if (!ed) return;
    if (value !== lastValueRef.current) {
      ed.setData(value || '');
      lastValueRef.current = value || '';
    }
  }, [value]);


  const toggleFullscreen = () => {
    const next = !isFullscreen;
    setIsFullscreen(next);
    document.body.style.overflow = next ? 'hidden' : '';
  };


  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        document.body.style.overflow = '';
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  return (
    <div
      ref={wrapperRef}
      className={`ck-editor-wrapper ${isFullscreen ? 'ck-editor-wrapper--fullscreen' : ''}`}
      style={{ minHeight: height, position: 'relative' }}
    >
      <button
        type="button"
        aria-label={isFullscreen ? 'Esci da schermo intero' : 'Schermo intero'}
        title={isFullscreen ? 'Esci da schermo intero' : 'Schermo intero'}
        onClick={toggleFullscreen}
        className="ck-fs-btn"
      >
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d={isFullscreen ? ICON_EXIT : ICON_ENTER} fill="currentColor" />
        </svg>
      </button>

      <div ref={hostRef} />

      <style>{`
        .ck-editor-wrapper .ck-editor__editable {
          min-height: ${height - 60}px;
        }
        .ck-editor-wrapper .ck-fs-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          z-index: 2;
          border: none;
          background: transparent;
          color: #444;
          padding: 6px;
          border-radius: 6px;
          cursor: pointer;
        }
        .ck-editor-wrapper .ck-fs-btn:hover {
          background: rgba(0,0,0,0.06);
        }
        .ck-editor-wrapper--fullscreen {
          position: fixed !important;
          inset: 0;
          z-index: 1060;
          background: var(--bs-body-bg, #fff);
          padding: 12px;
          display: flex;
          flex-direction: column;
        }
        .ck-editor-wrapper--fullscreen .ck-editor {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        .ck-editor-wrapper--fullscreen .ck-editor__editable {
          min-height: calc(100vh - 140px);
        }
      `}</style>
    </div>
  );
}
